import React, { useState, useContext, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTicketAlt, FaTimes, FaSpinner, FaGift, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import axios from 'axios';
import { UserContext } from './App';

const RedeemCodeModal = ({ isOpen, onClose }) => {
  const { user, setUser } = useContext(UserContext);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success' | 'error', message: string }

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCode('');
      setStatus(null);
      setLoading(false);
    }
  }, [isOpen]);

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!code.trim() || !user) return;

    setLoading(true);
    setStatus(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('https://test3-0ar4.onrender.com/api/user/redeem-promocode', 
        { code: code.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStatus({ type: 'success', message: response.data.message });
        if (setUser) {
          setUser(prev => ({ 
            ...prev, 
            promoBalance: response.data.newPromoBalance 
          }));
        }
      }
    } catch (err) {
      setStatus({ 
        type: 'error', 
        message: err.response?.data?.error || 'Failed to redeem code. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="redeem-modal-overlay" onClick={onClose}>
          <motion.div 
            className="redeem-modal-content"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 800 }}>Promo code</h3>
              <button className="close-btn" onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#6b7280', fontSize: '16px', cursor: 'pointer' }}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleRedeem} className="redeem-form">
               <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                  <div style={{ width: '64px', height: '64px', color: '#ffb703', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px' }}>
                     <FaGift />
                 </div>
              </div>

              <div className="input-wrapper">
                <input 
                  type="text" 
                  placeholder="Enter promo code" 
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  disabled={loading || status?.type === 'success'}
                  autoFocus
                />
              </div>

              {status && (
                <motion.div 
                  className={`status-message ${status.type}`}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                >
                  {status.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
                  <span>{status.message}</span>
                </motion.div>
              )}

              <button 
                type="submit" 
                className="submit-btn" 
                disabled={loading || !code.trim() || status?.type === 'success'}
              >
                {loading ? (
                  <><FaSpinner className="fa-spin" /> Processing...</>
                ) : status?.type === 'success' ? (
                  'Redeemed Successfully!'
                ) : (
                  'Redeem'
                )}
              </button>

              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                 <p style={{ color: '#d1d5db', fontSize: '12px', fontWeight: 700, margin: '0 0 16px 0' }}>Follow our <span style={{ color: '#fff', fontWeight: 900 }}>Socials</span> to claim all Promo Codes</p>
                 <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                      <div 
                        onClick={() => window.open('https://discord.gg/7dayErEGt7', '_blank')}
                        style={{ width: '40px', height: '40px', background: '#252731', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', cursor: 'pointer' }}
                      >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3333-.9555 2.419-2.1569 2.419zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3333-.946 2.419-2.1568 2.419z"/></svg>
                      </div>
                      <div 
                        onClick={() => window.open('https://kick.com/rustycoincom', '_blank')}
                        style={{ width: '40px', height: '40px', background: '#252731', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', cursor: 'pointer', fontWeight: 900, fontSize: '18px' }}
                      >
                          K
                      </div>
                 </div>
              </div>
            </form>
          </motion.div>

          <style jsx>{`
            .redeem-modal-overlay {
              position: fixed;
              inset: 0;
              background: rgba(0, 0, 0, 0.85);
              backdrop-filter: blur(4px);
              z-index: 10000;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 20px;
            }

            .redeem-modal-content {
              width: 100%;
              max-width: 400px;
              background: #1a1c22;
              border: 1px solid #ffb703;
              border-radius: 12px;
              padding: 24px;
              box-shadow: 0 0 20px rgba(255, 183, 3, 0.1);
              position: relative;
            }

            .modal-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 24px;
            }

            .redeem-form {
              display: flex;
              flex-direction: column;
            }

            .input-wrapper input {
              width: 100%;
              background: #16191f;
              border: 1px solid #374151;
              border-radius: 4px;
              padding: 16px;
              color: #fff;
              font-size: 14px;
              font-weight: 600;
              transition: all 0.2s;
            }

            .input-wrapper input:focus {
              border-color: #ffb703;
              outline: none;
            }

            .status-message {
              padding: 12px 16px;
              border-radius: 4px;
              display: flex;
              align-items: center;
              gap: 10px;
              font-size: 13px;
              font-weight: 600;
              overflow: hidden;
            }

            .status-message.success {
              background: rgba(16, 185, 129, 0.1);
              color: #10b981;
              border: 1px solid rgba(16, 185, 129, 0.2);
            }

            .status-message.error {
              background: rgba(239, 68, 68, 0.1);
              color: #ef4444;
              border: 1px solid rgba(239, 68, 68, 0.2);
            }

            .submit-btn {
              background: #ffb703;
              color: #000;
              border: none;
              border-radius: 4px;
              padding: 14px;
              font-size: 14px;
              font-weight: 900;
              text-transform: uppercase;
              cursor: pointer;
              transition: all 0.2s;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
            }

            .submit-btn:hover:not(:disabled) {
              background: #ffb703;
            }

            .submit-btn:disabled {
              opacity: 0.6;
              cursor: not-allowed;
              filter: grayscale(0.5);
            }

            .fa-spin {
              animation: spin 1s linear infinite;
            }

            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
    </AnimatePresence>
  );
};

export default RedeemCodeModal;
